import { useCallback, useSyncExternalStore } from 'react';
import { type OrgAssetResponse } from '@goldcast/api/content';
import OverlayUploadSection from './OverlayUploadSection';
import { useVideoOverlay } from '@/Pages/Clip/ClipPlayer/ClipAssets/VideoOverlay/useVideoOverlay';
import { VideoOverlayTrack } from '@/Pages/Clip/ClipPlayer/ClipAssets/VideoOverlay/types';
import { timelineActionsStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/timelineActions';

interface VideoTabContentProps {
  closePopup: () => void;
  recentAssets: OrgAssetResponse[];
}

const VideoTabContent = ({ closePopup, recentAssets }: VideoTabContentProps) => {
  const { isAddingVideo } = useSyncExternalStore(timelineActionsStore.subscribe, timelineActionsStore.getSnapshot);
  const { uploadVideo, acceptedFormats, selectLibraryVideo } = useVideoOverlay({ onUploadComplete: closePopup });

  const handleRecentVideoClick = useCallback(
    async (asset: OrgAssetResponse) => {
      // OrgAssetResponse has no thumbnail or duration metadata.
      // selectLibraryVideo ignores thumbnailUrl and fetches duration from the video URL when 0.
      const videoTrack: VideoOverlayTrack = {
        id: asset.id,
        name: asset.name,
        videoUrl: asset.source_url,
        thumbnailUrl: '',
        duration: 0,
        tags: []
      };
      await selectLibraryVideo(videoTrack);
      closePopup();
    },
    [selectLibraryVideo, closePopup]
  );

  return (
    <OverlayUploadSection
      isAdding={isAddingVideo}
      uploadTitle="Upload Video"
      uploadLabel="Click to browse or drag and drop a video"
      accept={acceptedFormats}
      onFilesUpload={uploadVideo}
      recentAssets={recentAssets}
      onAssetClick={handleRecentVideoClick}
      renderAssetContent={asset => (
        <video src={asset.source_url} className="size-full object-cover" preload="metadata" muted playsInline />
      )}
      testIdPrefix="video"
    />
  );
};

export default VideoTabContent;
